import { useState, useEffect } from 'react';
import { Col, Form, Input, Row, Spinner, Label } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../helpers/datePickerConfig';
import Select from 'react-select';
import { Search, ChevronDown, ChevronUp, Plus, Download } from 'react-feather';
import { isDateAfter, isDateBefore, formatDate } from '../../../helpers/dateUtils';
import Table from '../../ui/Table';
import facturasApiService from '../../../services/http/facturasApiService';
import logger from '../../../services/logger';
import tableColumns from './constants';
import ClearIndicator from './ClearIndicator';
import colors from '../../../theme/colors';

const FormularioFacturas = () => {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(true);
  const [clientsList, setClientsList] = useState('');
  const [selectedClientList, setSelectedClientList] = useState([]);
  const [isFormSubmittable, setIsFormSubmittable] = useState(true);
  const [isTableNotLoading, setIsTableNotLoading] = useState(true);
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFinal, setFechaFinal] = useState(null);
  const [fechaError, setFechaError] = useState('');
  const [headquarter, setHeadquarter] = useState('CEDESCA');
  const [idRecibo, setIdRecibo] = useState({ data: '', error: '' });
  const [data, setData] = useState(null);
  const [name, setName] = useState({ data: '', error: '' });
  const [lastname, setLastname] = useState({ data: '', error: '' });
  const [document, setDocument] = useState({ data: '', error: '' });
  const [abonado, setAbonado] = useState(false);

  const handleAbonadoChange = (e) => setAbonado(e.target.checked);

  const handleRowDoubleClick = (row) => {
    let { centro } = row.original;
    const { factura } = row.original;
    if (centro === 'CNTEC Girona') {
      centro = 'CNTEC';
    } else if (!centro || centro === null || centro === '') {
      centro = headquarter;
    }
    navigate(`/facturas/${centro}/${factura}/detalles/`);
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const dataClientes = await facturasApiService.cargarClientes({ headquarter: 'CEDESCA' });
        dataClientes.unshift({ id: '', value: 'Todos' });
        const opcionesRenombradas = dataClientes.map((opcion) => ({ value: opcion.id, label: opcion.value }));
        setClientsList(opcionesRenombradas);
        setIsFormSubmittable(true);
      } catch (error) {
        setClientsList([]);
        setIsFormSubmittable(false);
      }
    };
    fetch();
  }, []);

  const handleClientsList = (selected) => setSelectedClientList(selected);
  const handleHeadquarter = (e) => setHeadquarter(e.target.value);
  const handleDocumentOnChange = (e) => setDocument({ data: e.target.value.trim(), error: '' });

  const handleFechaInicio = (value) => {
    const nuevaFechaInicio = value ? (value instanceof Date ? value : new Date(value)) : null;
    if (!nuevaFechaInicio) {
      setFechaFinal(null);
      setFechaError('');
    } else if (fechaFinal && isDateAfter(nuevaFechaInicio, fechaFinal)) {
      setFechaError('La fecha de inicio no puede ser posterior a la fecha final.');
    } else {
      setFechaError('');
    }
    setFechaInicio(nuevaFechaInicio);
  };

  const handleFechaFinal = (value) => {
    const nuevaFechaFinal = value instanceof Date ? value : new Date(value);
    if (fechaInicio && isDateBefore(nuevaFechaFinal, fechaInicio)) {
      setFechaError('La fecha final no puede ser anterior a la fecha de inicio.');
    } else {
      setFechaError('');
    }
    setFechaFinal(nuevaFechaFinal);
  };

  const handleOnSubmit = async (e) => {
    e.preventDefault();
    setIsTableNotLoading(false);
    const clientes = selectedClientList.map((client) => client.value).join(',');
    const params = {
      empresaId: headquarter,
      nombre: name.data,
      apellido: lastname.data,
      documento: document.data,
      idRecibo: idRecibo.data,
      fechaI: fechaInicio ? formatDate(fechaInicio, 'yyyy-MM-dd') : '',
      fechaF: fechaFinal ? formatDate(fechaFinal, 'yyyy-MM-dd') : '',
      excel: false,
      clientes,
      abonado,
    };
    try {
      const actualData = await facturasApiService.buscarListadoFacturas(params);
      setData([...actualData]);
    } catch (error) {
      // no-op
    } finally {
      setIsTableNotLoading(true);
    }
  };

  const downloadExcel = async (e) => {
    e.preventDefault();
    setIsTableNotLoading(false);
    const params = {
      empresaId: headquarter,
      nombre: name.data,
      apellido: lastname.data,
      documento: document.data,
      idRecibo: idRecibo.data,
      fechaI: fechaInicio ? formatDate(fechaInicio, 'yyyy-MM-dd') : '',
      fechaF: fechaFinal ? formatDate(fechaFinal, 'yyyy-MM-dd') : '',
      excel: true,
      abonado,
    };
    try {
      const dataSolicitudes = await facturasApiService.buscarListadoFacturas(params);
      const url = window.URL.createObjectURL(dataSolicitudes);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `facturas_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Error al descargar el archivo:', error);
    } finally {
      setIsTableNotLoading(true);
    }
  };

  const handleNameOnChange = (e) => {
    if (e.target.value.trim().length === 0) {
      setName({ data: '', error: '' });
      setIsFormSubmittable(true);
    } else if (!e.target.value.trim().match(/^(?![\s.]+$)[a-zA-ZÀ-ÿ\s.]*$/)) {
      setName({ data: '', error: 'format' });
      setIsFormSubmittable(false);
    } else if (e.target.value.trim().length === 1) {
      setName({ data: '', error: 'length' });
      setIsFormSubmittable(false);
    } else {
      setName({ data: e.target.value.trim(), error: '' });
      setIsFormSubmittable(true);
    }
  };

  const handleLastNameOnChange = (e) => {
    if (!e.target.value.trim().match(/^(?![\s.]+$)[a-zA-ZÀ-ÿ\s.]*$/)) {
      setLastname({ data: '', error: 'format' });
      setIsFormSubmittable(false);
    } else if (e.target.value.trim().length === 1) {
      setLastname({ data: '', error: 'length' });
      setIsFormSubmittable(false);
    } else {
      setLastname({ data: e.target.value.trim(), error: '' });
      setIsFormSubmittable(true);
    }
  };

  const handleIDOnChange = (e) => {
    if (e.target.value.trim().length === 0) {
      setIdRecibo({ data: '', error: '' });
      setIsFormSubmittable(true);
    } else if (e.target.value.trim().match(/^[^0-9]*$/)) {
      setIdRecibo({ data: '', error: 'format' });
      setIsFormSubmittable(false);
    } else {
      setIdRecibo({ data: e.target.value.trim(), error: '' });
      setIsFormSubmittable(true);
    }
  };

  const toolbarButtons = (
    <>
      <button
        type="button"
        onClick={() => navigate('/facturas/nueva')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
          fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
          border: `1px solid ${colors.primary}`, backgroundColor: '#fff',
          color: colors.primary, cursor: 'pointer', transition: 'all 0.15s',
        }}
      >
        <Plus size={13} />
        Añadir
      </button>
      <button
        type="submit"
        disabled={!isFormSubmittable || !isTableNotLoading}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
          fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px', border: 'none',
          backgroundColor: colors.primary, color: '#fff',
          cursor: (!isFormSubmittable || !isTableNotLoading) ? 'not-allowed' : 'pointer',
          opacity: (!isFormSubmittable || !isTableNotLoading) ? 0.5 : 1, transition: 'all 0.15s',
        }}
      >
        {!isTableNotLoading ? <Spinner style={{ height: '13px', width: '13px' }} /> : <Search size={13} />}
        {!isTableNotLoading ? 'Buscando...' : 'Buscar'}
      </button>
      <button
        type="button"
        onClick={downloadExcel}
        disabled={data === null || !isTableNotLoading}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 10px',
          fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
          border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#6b7280',
          cursor: (data === null || !isTableNotLoading) ? 'not-allowed' : 'pointer',
          opacity: (data === null || !isTableNotLoading) ? 0.5 : 1, transition: 'all 0.15s',
        }}
      >
        <Download size={13} />
      </button>
    </>
  );

  return (
    <>
      <Form onSubmit={handleOnSubmit}>
        <div style={{
          backgroundColor: '#fff', borderRadius: '12px',
          border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgb(0 0 0 / 0.06)',
          overflow: 'visible',
        }}>
          {/* Toolbar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: showFilters ? '1px solid #f3f4f6' : 'none',
          }}>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: 600, color: '#374151',
                padding: '4px 8px', borderRadius: '6px',
              }}
            >
              <Search size={15} style={{ color: '#9ca3af' }} />
              Filtros
              {showFilters
                ? <ChevronUp size={14} style={{ color: '#9ca3af' }} />
                : <ChevronDown size={14} style={{ color: '#9ca3af' }} />}
            </button>
            <div style={{ display: 'flex', gap: '8px' }}>
              {toolbarButtons}
            </div>
          </div>

          {/* Filtros colapsables */}
          {showFilters && (
            <div style={{
              padding: '16px',
              opacity: !isTableNotLoading ? 0.5 : 1,
              pointerEvents: !isTableNotLoading ? 'none' : 'auto',
              transition: 'opacity 0.2s',
            }}>
              <Row className="mb-3">
                <Col sm="2">
                  <label htmlFor="name" className="form-label">Nombre</label>
                  <Input
                    type="text" name="name" id="name" placeholder="Buscar por nombre"
                    onChange={handleNameOnChange} onInput={handleNameOnChange}
                    className={name.error !== '' ? 'border border-danger error' : ''}
                    defaultValue={name.data}
                  />
                </Col>
                <Col sm="3">
                  <label htmlFor="lastname" className="form-label">Apellidos</label>
                  <Input
                    type="text" name="lastname" id="lastname" placeholder="Buscar por apellidos"
                    onChange={handleLastNameOnChange} onInput={handleLastNameOnChange}
                    className={lastname.error !== '' ? 'border border-danger error' : ''}
                    defaultValue={lastname.data}
                  />
                </Col>
                <Col sm="2">
                  <label htmlFor="idRecibo" className="form-label">Número de factura</label>
                  <Input
                    type="text" name="idRecibo" id="idRecibo" placeholder="Buscar por número de factura"
                    onChange={handleIDOnChange} onInput={handleIDOnChange}
                    className={idRecibo.error !== '' ? 'border border-danger error' : ''}
                    defaultValue={idRecibo.data}
                  />
                </Col>
                <Col sm="3">
                  <label htmlFor="documento" className="form-label">Documento</label>
                  <Input
                    type="text" name="documento" id="documento" placeholder="Buscar por documento"
                    onChange={handleDocumentOnChange} onInput={handleDocumentOnChange}
                    className={document.error !== '' ? 'border border-danger error' : ''}
                    defaultValue={document.data}
                  />
                </Col>
                <Col sm="2">
                  <label htmlFor="centros" className="form-label">Empresa</label>
                  <select className="form-select" name="centros" id="centros" onChange={handleHeadquarter} value={headquarter}>
                    <option value="CEDESCA">CEDESCA</option>
                    <option value="CNTEC">CNTEC</option>
                  </select>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col sm="3">
                  <Label for="fechaInicio">Fecha de Inicio</Label>
                  <DatePicker
                    id="fechaInicio" name="fechaInicio" selected={fechaInicio}
                    onChange={handleFechaInicio} dateFormat="dd/MM/yyyy" locale="es"
                    className="form-control" wrapperClassName="w-100"
                  />
                </Col>
                <Col sm="3">
                  <Label for="fechaFinal">Fecha Final</Label>
                  <DatePicker
                    id="fechaFinal" name="fechaFinal" selected={fechaFinal}
                    onChange={handleFechaFinal} dateFormat="dd/MM/yyyy" locale="es"
                    className="form-control" disabled={!fechaInicio} minDate={fechaInicio}
                    wrapperClassName="w-100"
                  />
                  {fechaError && <span className="text-danger tiny">{fechaError}</span>}
                </Col>
                <Col sm="5">
                  <label htmlFor="clientes" className="form-label">Clientes</label>
                  <Select
                    options={clientsList} closeMenuOnSelect={false} value={selectedClientList}
                    isMulti onChange={handleClientsList} components={{ ClearIndicator }}
                    placeholder="Seleccionar..."
                  />
                </Col>
              </Row>
              <Row>
                <Col sm="2" className="d-flex align-items-center">
                  <div className="form-check">
                    <Input
                      type="checkbox" id="abonado" name="abonado" className="form-check-input"
                      checked={abonado} onChange={handleAbonadoChange}
                    />
                    <Label className="form-check-label ms-1" for="abonado">Sólo abonados</Label>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </Form>

      {data !== null && (
        <div style={{ marginTop: '16px' }}>
          <Table
            columns={tableColumns}
            data={data}
            defaultPageSize={10}
            showPaginationBottom
            className="-striped -highlight text-center"
            getTrProps={(state, rowInfo) => ({
              onDoubleClick: () => handleRowDoubleClick(rowInfo),
            })}
          />
        </div>
      )}
    </>
  );
};

export default FormularioFacturas;
